import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../ui';
import { serializeFilters } from '../filters/serialization';
import { FilterOperator } from '../filters/types';

export interface MetadataRow {
  label: string;
  value?: string | null;
  /** filter to apply on /bhajans when the user clicks this row's value */
  edgeName?: string;
  edgeNodeId?: number;
}

export interface MetadataTableProps {
  rows: MetadataRow[];
  loading?: boolean;
  className?: string;
}

/** Tabular bhajan metadata, like jnana-admin's row inspector. Linkable values. */
export const MetadataTable: React.FC<MetadataTableProps> = ({ rows, loading, className }) => {
  const visible = rows.filter((r) => loading || (r.value && r.value.trim()));
  if (visible.length === 0) return null;

  return (
    <dl
      className={cn(
        'grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2',
        'rounded-2xl border border-light bg-bg-2/60 px-5 py-4',
        className
      )}
    >
      {visible.map((row, i) => {
        // Build the same filter URL the Browse drill-down uses (shared serializer
        // → base64 [{f,o,v}]). The narrowing check avoids non-null assertions.
        let filterParam: string | null = null;
        if (row.edgeName && row.edgeNodeId != null && row.value) {
          filterParam = encodeURIComponent(
            serializeFilters([
              {
                id: 'meta',
                filterId: 'edge',
                operator: FilterOperator.HasSpecific,
                value: { edgeName: row.edgeName, targetNodeIds: [row.edgeNodeId] },
              },
            ])
          );
        }

        return (
          <React.Fragment key={i}>
            <dt className="text-small italic text-text-tertiary self-center whitespace-nowrap">
              {row.label}
            </dt>
            <dd className="text-body text-text-primary self-center min-w-0 truncate capitalize">
              {loading ? (
                <span className="inline-block h-5 w-32 max-w-full bg-bg-3 rounded animate-pulse align-middle" />
              ) : filterParam ? (
                <Link
                  to={`/bhajans?filters=${filterParam}`}
                  className="hover:text-accent-gold transition-colors duration-150"
                >
                  {row.value}
                </Link>
              ) : (
                <span>{row.value}</span>
              )}
            </dd>
          </React.Fragment>
        );
      })}
    </dl>
  );
};
